import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { isType } from '@sorare/core/src/gql';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { cardAttributes } from '@sorare/core/src/lib/glossary';

import { RecommendationLastFifteen_anyPlayer } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: center;
  justify-content: center;
`;

type Props = {
  player: RecommendationLastFifteen_anyPlayer;
};

export const LastFifteen = ({ player }: Props) => {
  const {
    flags: { useMlbSeasonAverageScore },
  } = useFeatureFlags();

  const mode = useMemo(() => {
    if (isType(player, 'BaseballPlayer')) {
      return useMlbSeasonAverageScore
        ? AveragePlayerScore.SEASON_AVERAGE_SCORE
        : AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE;
    }
    if (isType(player, 'NBAPlayer')) {
      return AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;
    }
    return AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE;
  }, [player, useMlbSeasonAverageScore]);

  const label = {
    [AveragePlayerScore.SEASON_AVERAGE_SCORE]: (
      <FormattedMessage {...cardAttributes.season} />
    ),
    [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]: 'L15',
    [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]: 'L10',
    [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]: 'L5',
  }[mode];

  return (
    <Root>
      <BodyS as="span" color="var(--c-neutral-600)">
        {label}
      </BodyS>
      <PlayerScore player={player} mode={mode} />
    </Root>
  );
};

LastFifteen.fragments = {
  anyPlayer: gql`
    fragment RecommendationLastFifteen_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      ...PlayerScore_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<RecommendationLastFifteen_anyPlayer>,
};
